import { Outlet, useLocation } from "react-router-dom";
import React, { useEffect, useState, lazy, useMemo } from "react";
import { AutoComplete, Button, Input, Layout, SelectProps, Space, Tag, Typography } from "antd";
import { SideMenu } from "../protectedLayout/SideMenu";
import { useCheckAuthentication } from "../../../auth/hook/user-authentication.hook";
import styles from "./Layout.module.scss";
import { MenuFoldOutlined, MenuUnfoldOutlined, SearchOutlined } from "@ant-design/icons";
import { useLazyQuery } from "@apollo/client";
import { SearchItem } from "../../graphql/similarity-search.graphql";
import { SearchResult } from "../../type/search";
import { useNavigate } from 'react-router-dom';
import { ReceivedBackOrderCountProvider } from "../../../inventory/component/ReceivedBackOrderMenuItem";

const ReportComponent = lazy(
  async () => import("../../../../core/component/Report/ReportComponent")
);

const { Text } = Typography;

const { Sider, Content } = Layout;

const tagColorMap: Record<string, string> = {
  "Order No": "blue",
  "SKU": "green",
  "Contact Phone": "volcano",
  "Contact ID": "purple",
  "Appraisal No": "orange",
  "Contact Name": "cyan",
};

export const ProtectedLayout: React.FC = () => {
  const checkAsync = useCheckAuthentication();
  const [sideBarEnabled, setSideBarEnabled] = useState<boolean>(true);
  const navigate = useNavigate()
  useEffect(() => {
    checkAsync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const location = useLocation();
  const pathname = location.pathname || "";
  const characters = pathname.split("/");
  const [selectedItem, setSelectedItem] = useState<string>("");

  useEffect(() => {
    const selectedItem = (characters[characters.length - 2] === "" ? "" : capitalizeFirstWord(characters[characters.length - 2]) + " - ") + characters[characters.length - 1]
      .split("-")
      .map((word, index) =>
        index === 0
          ? word.charAt(0).toUpperCase() + word.slice(1)
          : word.charAt(0).toUpperCase() + word.slice(1)
      )
      .join(" ");
    setSelectedItem(selectedItem);
  }, [characters]);

  const capitalizeFirstWord = (str: string): string => {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const [options, setOptions] = useState<SearchResult[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [search, setSearch] = useState<string>('');
  const [execute, { loading }] = useLazyQuery(SearchItem, {
    fetchPolicy: 'network-only',
    variables: {
      request: {
        search: search,
      },
    },
    onCompleted: (result) => {
      const items = (result?.posSimilaritySearch || []) as SearchResult[];
      const newOptions: SelectProps["options"] = items.map((item: SearchResult) => ({
        value: item.value,
        label: (
          <div className="d-flex flex-wrap w-100 justify-content-between">
            <div>
              <Text strong>{item.value}</Text> &nbsp;
              <div style={{ fontSize: 12, color: "#888" }}>{item.metadata.subtitle}</div>
            </div>
            <div>
              <Tag color={tagColorMap[item?.metadata?.tag] || "default"} style={{ marginTop: 4 }}>
                {item?.metadata?.tag}
              </Tag>
            </div>
          </div>
        ),
      }));

      setOptions(items);
    },
  });

  const handleSearch = (inputValue: string) => {
    setSearch(inputValue);

    // Auto exinputValueecute search with a small delay to avoid too many requests
    if (inputValue.trim() && inputValue.length >= 3) {
      setTimeout(() => {
        execute();
      }, 500);
    } else {
      setSearch("");
    }
  };

  const handleOptionClick = (item: SearchResult) => {
    switch (item?.metadata?.tag) {
      case "Order No":
        window.history.pushState({}, '', `/order/${item.value}`);
        window.dispatchEvent(new PopStateEvent('popstate'));
        setShowDropdown(false);
        break;

      case "SKU":
        window.history.pushState({}, '', `/inventory/${item.value}`);
        window.dispatchEvent(new PopStateEvent('popstate'));
        setShowDropdown(false);
        break;

      case "Contact Phone":
        window.history.pushState({}, '', `/contact/${item?.metadata?.contactId}`);
        window.dispatchEvent(new PopStateEvent('popstate'));
        setShowDropdown(false);
        break;

      case "Contact ID":
        window.history.pushState({}, '', `/contact/${item.value}`);
        window.dispatchEvent(new PopStateEvent('popstate'));
        setShowDropdown(false);
        break;

      case "Appraisal No":
        window.history.pushState({}, '', `/appraisal/${item.value}`);
        window.dispatchEvent(new PopStateEvent('popstate'));
        setShowDropdown(false);
        break;

      case "Contact Name":
        window.history.pushState({}, '', `/contact/${item?.metadata?.contactId}`);
        window.dispatchEvent(new PopStateEvent('popstate'));
        setShowDropdown(false);
        break;

      default:
        break;
    }
  };

  return (
    <ReceivedBackOrderCountProvider>
    <Layout className={styles.protectedContainer}>
      <Sider
        collapsible
        collapsed={sideBarEnabled}
        onCollapse={setSideBarEnabled}
        width={250}
        trigger={null}
      >
        <SideMenu sideBarEnabled={sideBarEnabled} />
      </Sider>
      <Layout className="bg-gray">
        <div
          className={
            "bg-gray d-flex p-2 d-flex flex-row" + styles.headerContainer
          }
        >
          <div className="w-100 d-flex flex-row gap-3">
            <Button
              type="primary"
              onClick={() => setSideBarEnabled(!sideBarEnabled)}
              style={{ marginBottom: 16 }}
            >
              {sideBarEnabled ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            </Button>
            <div className="text-center mt-2">
              <p className="f-xlarge">{selectedItem}</p>
            </div>
          </div>
          <Space className="justify-content-end w-100" size="large"></Space>
          <div>
            <div style={{ width: 400, margin: "0 auto", position: "relative" }}>
              <Input
                className="similarity-search"
                placeholder="Enter 3 or more to search"
                onChange={(e) => {
                  handleSearch(e.target.value);
                  setShowDropdown(!!e.target.value);
                }}
                prefix={<SearchOutlined />}
                onBlur={() => {
                  setTimeout(() => setShowDropdown(false), 200);
                }}
                onFocus={(e) => {
                  if (e.target.value) setShowDropdown(true);
                }}
              />

              {showDropdown && options?.length > 0 && (
                <div
                  style={{
                    position: "absolute",
                    top: 60,
                    left: 0,
                    right: 0,
                    background: "#fff",
                    border: "1px solid #ccc",
                    borderRadius: 6,
                    zIndex: 1000,
                    maxHeight: 250,
                    overflowY: "auto",
                    boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
                  }}
                >
                  {options.map((item, index) => (
                    <div
                      key={index}
                      className="d-flex flex-wrap w-100 justify-content-between"
                      style={{
                        padding: 10,
                        borderBottom: "1px solid #eee",
                        cursor: "pointer",
                      }}
                      onClick={() => handleOptionClick(item)}
                    >
                      <div>
                        <Text strong>{item.value}</Text>
                        <div style={{ fontSize: 12, color: "#888" }}>{item.metadata.subtitle}</div>
                      </div>
                      <Tag color={tagColorMap[item?.metadata?.tag] || "default"} style={{ marginTop: 4, height: '24px' }}>
                        {item?.metadata?.tag}
                      </Tag>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <Content className={"px-2 " + styles.protectedContent}>
          <ReportComponent />
          <Outlet />
        </Content>
      </Layout>
    </Layout>
    </ReceivedBackOrderCountProvider>
  );
};
